<template>
  <m-select
    ref="input"
    class="user-picker"
    :full-width="fullWidth"
    :hide-border="hideBorder"
    :m-style="mStyle"
    :items="options"
    :value="transformedValue"
    :placeholder="placeholder"
    :hide-placeholder="hidePlaceholder"
    :placeholder-icon="placeholderIcon"
    :loading="loading"
    :hide-hover="hideHover"
    :max-tag-text-length="maxTagTextLength"
    :disabled="disabled"
    :read-only="readOnly"
    :small="small"
    :popup="popup"
    :tags="tags"
    :show-search="showSearch"
    :nowrap="nowrap"
    :truncate-selected-items="truncateSelectedItems"
    :hide-selected-values="popup"
    :hide-arrow="hideArrow"
    :match-trigger-width="matchTriggerWidth"
    show-description
    :description="$t('userPicker.description')"
    :auto-focus="autoFocus"
    :has-error="hasError"
    value-key="value"
    item-text="text"
    multiple
    keep-open-on-click
    cache-items
    @change="emitUpdate"
    @close="$emit('close')"
  >
    <template #trigger>
      <slot name="trigger" />
    </template>
    <template #overlay-header>
      <slot name="overlay-header" />
    </template>
    <template #overlay>
      <slot name="overlay" />
    </template>
    <template #item="item">
      <user-display
        :user="selectSingle(item.item)"
        :small="!small || !item.isTrigger"
        :xs="small && item.isTrigger"
      />
    </template>
  </m-select>
</template>

<script>
import UserDisplay from 'shared/components/UserDisplay.vue';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useUsers from '@/composables/user/users';
import { computed } from 'vue';
import { mStyleProps } from 'shared/lib/m-style-props';
import { uniqBy } from 'lodash-es';

export default {
  name: 'UserPicker',
  props: {
    ...mStyleProps,
    value: {
      type: Array,
      default: () => [],
    },
    maxTagTextLength: {
      default: () => 0,
      type: Number,
    },
    placeholderIcon: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    hidePlaceholder: {
      type: Boolean,
      default: false,
    },
    hideHover: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    nowrap: {
      type: Boolean,
      default: false,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    tags: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    hideArrow: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    hideBorder: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    matchTriggerWidth: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    excludedUsers: {
      type: Array,
      default: () => [],
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    truncateSelectedItems: {
      type: Number,
      default: -1,
    },
  },
  setup() {
    const { selectSingle, selectMultiple, users, archivedUsers } = useUsers();
    const { loggedInUser } = useLoggedInUser();

    return {
      stateUsers: computed(() => [...users.value, ...archivedUsers.value]),
      selectSingle,
      selectMultiple,
      loggedInUser,
    };
  },
  emits: ['input', 'update:value', 'change', 'close'],
  components: { UserDisplay },
  data() {
    return {
      search: '',
      loading: false,
      selectedUsers: [],
    };
  },
  computed: {
    transformedValue() {
      return this.value.map((v) => v.uid);
    },
    excludedUserIds() {
      return this.excludedUsers.map((u) => u.uid);
    },
    options() {
      return this.users.filter((u) => !this.excludedUserIds.includes(u.uid)).map((user) => ({
        text: this.getDisplayName(user),
        value: user.uid,
        disabled: user.archivedAt !== null,
        user,
      }));
    },
    users() {
      if (this.loggedInUser === null) {
        return this.stateUsers;
      }
      return uniqBy([this.loggedInUser, ...this.stateUsers], 'uid');
    },
  },
  methods: {
    show() {
      this.$refs.input.show();
    },
    getDisplayName(user) {
      if (user.firstName === '' && user.lastName === '') {
        return user.email;
      }
      return `${user.firstName} ${user.lastName}`;
    },
    emitUpdate(value) {
      const users = this.selectMultiple(value);
      this.$emit('change', users);
      this.$emit('input', users);
      this.$emit('update:value', users);
    },
  },
};
</script>

<style
scoped
    lang="scss"
    type="text/scss"
>
</style>
